import { useCallback, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import { FaAngleLeft, FaAngleRight, FaEdit, FaArrowLeft } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { PAGE_SIZE } from "../Utils/constant";
import TextInput from "../common/TextInput";
import { supabase } from "../../supabaseClient";
import HeaderContainer from "../CrewDashboard/HeaderContainer";

const ManagerDrivers = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [isEdit, setIsEdit] = useState(false);
  const [driver, setDriver] = useState(null);
  const [search, setSearch] = useState(() => {
    // Restore search state from sessionStorage if it exists
    const savedSearch = sessionStorage.getItem("managerDriversSearch");
    if (savedSearch) {
      try {
        return JSON.parse(savedSearch);
      } catch {
        return null;
      }
    }
    return null;
  });
  const [driversData, setDriversData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [companies, setCompanies] = useState([]);
  const [currentManagerId, setCurrentManagerId] = useState(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      CompanyID: "",
      DriverCode: "",
      FirstName: "",
      LastName: "",
      Email: "",
      Active: true,
    },
  });

  useEffect(() => {
    // Get current manager's UserID from session
    const userID = parseInt(sessionStorage.getItem("UserId"));
    setCurrentManagerId(userID);
    
    // If there was a saved search, trigger it after component mounts
    const savedSearch = sessionStorage.getItem("managerDriversSearch");
    if (savedSearch) {
      try {
        const parsed = JSON.parse(savedSearch);
        if (parsed && parsed.type && parsed.text) {
          // Search state is already set from the useState initialization
          // The search will be triggered by the useEffect that watches search state
        }
      } catch {
        // Ignore parse errors
      }
    }
  }, []);

  useEffect(() => {
    if (driver) {
      reset({
        CompanyID: driver.CompanyID,
        DriverCode: driver.DriverCode,
        FirstName: driver.FirstName,
        LastName: driver.LastName,
        Email: driver.Email,
        Active: driver.Active === 1 || driver.Active === true,
      });
    }
  }, [driver, reset]);

  useEffect(() => {
    const fetchCompanies = async () => {
      const { data: Companies, error: cError } = await supabase
        .from("Companies")
        .select("CompanyID, Name");
      if (cError) {
        setCompanies([]);
      } else {
        setCompanies(Companies);
        reset((prev) => ({
          ...prev,
          CompanyID: Companies && Companies[0] ? Companies[0].CompanyID : "",
        }));
      }
    };
    fetchCompanies();
  }, [reset]);

  useEffect(() => {
    const fetchDrivers = async () => {
      if (!currentManagerId) {
        return;
      }
      
      let query = supabase
        .from("driver_auth")
        .select(
          `
          id,
          email,
          is_active,
          driver_profiles!inner (
            id,
            first_name,
            last_name,
            phone,
            license_number,
            employment_status,
            manager_id
          )
        `,
          { count: "exact", head: false }
        )
        .eq("is_active", true)
        .eq("driver_profiles.manager_id", currentManagerId)
        .range((page - 1) * PAGE_SIZE, page * PAGE_SIZE - 1);

      if (search && search.text) {
        if (search.type === "FirstName" || search.type === "LastName") {
          query = query.ilike(
            `driver_profiles.${search.type.toLowerCase()}`,
            `%${search.text}%`
          );
        } else if (search.type === "Email") {
          query = query.ilike("email", `%${search.text}%`);
        } else if (search.type === "DriverCode") {
          query = query.ilike(
            "driver_profiles.license_number",
            `%${search.text}%`
          );
        }
      }

      const { data, count, error } = await query;

      if (!error) {
        const transformedData =
          data?.map((driver) => ({
            DriverID: driver.id,
            CompanyID: null,
            DriverCode: driver.driver_profiles?.[0]?.license_number || "",
            FirstName: driver.driver_profiles?.[0]?.first_name || "",
            LastName: driver.driver_profiles?.[0]?.last_name || "",
            Email: driver.email,
            Active: driver.is_active ? 1 : 0,
            ManagerID: driver.driver_profiles?.[0]?.manager_id || null,
          })) || [];

        setDriversData(transformedData);
        setTotalCount(count || 0);
      }
    };
    fetchDrivers();
  }, [page, search, isEdit, currentManagerId]);

  const onNextPage = useCallback(() => {
    setPage((old) => old + 1);
  }, []);
  
  const onPrevPage = useCallback(() => {
    setPage((old) => Math.max(old - 1, 1));
  }, []);

  const onSuccess = useCallback(() => {
    toast.success("Success");
    setIsEdit(false);
    setDriver(null);
  }, []);
  
  const onError = useCallback((err) => {
    toast.error("Error: " + err.message);
  }, []);

  const onAddNew = () => {
    navigate("/driver-new");
  };

  const OnCancel = () => {
    setIsEdit(false);
    setDriver(null);
  };

  const OnCreate = useCallback(
    async (data) => {
      if (driver) {
        // Update existing driver
        const { error: authError } = await supabase
          .from("driver_auth")
          .update({
            email: data.Email,
            is_active: data.Active,
          })
          .eq("id", driver.DriverID);

        if (authError) {
          onError(authError);
          return;
        }

        const { error: profileError } = await supabase
          .from("driver_profiles")
          .upsert({
            auth_id: driver.DriverID,
            first_name: data.FirstName,
            last_name: data.LastName,
            license_number: data.DriverCode,
            phone: data.Phone || "",
            manager_id: currentManagerId, // Always keep current manager
          });

        if (profileError) onError(profileError);
        else onSuccess();
      } else {
        // Create new driver - automatically assign to current manager
        const { data: driverAuth, error: authError } = await supabase
          .from("driver_auth")
          .insert({
            email: data.Email,
            password_hash: "default_password",
            is_active: data.Active,
          })
          .select();

        if (authError) {
          onError(authError);
          return;
        }

        const { error: profileError } = await supabase
          .from("driver_profiles")
          .insert({
            auth_id: driverAuth[0].id,
            first_name: data.FirstName,
            last_name: data.LastName,
            license_number: data.DriverCode,
            phone: data.Phone || "",
            manager_id: currentManagerId, // Automatically assign to current manager
          });

        if (profileError) onError(profileError);
        else onSuccess();
      }
    },
    [driver, onError, onSuccess, currentManagerId]
  );

  const OnSearch = useCallback((data) => {
    setSearch(data);
    // Save search state to sessionStorage
    sessionStorage.setItem("managerDriversSearch", JSON.stringify(data));
  }, []);

  const handleBack = () => {
    // Clear search state when navigating away
    sessionStorage.removeItem("managerDriversSearch");
    navigate("/crew-dashboard");
  };

  return (
    <div className="p-6 w-full">
      <HeaderContainer />
      <div className="mt-5">
        <div
          onClick={handleBack}
          className="flex cursor-pointer justify-between items-center w-20 back-button mb-4"
        >
          <FaArrowLeft />
          <span>Back</span>
        </div>
        
        {isEdit ? (
          <div className="driver-patent-edit-content mt-4 rounded-lg">
            <h5 className="mb-4">{driver ? "Edit Driver" : "Add New Driver"}</h5>
            <div className="flex justify-between mb-2">
              <div>Company</div>
              <div className="w-35">
                <Controller
                  control={control}
                  name="CompanyID"
                  rules={{ required: "Required" }}
                  render={({ field: { value, onChange } }) => (
                    <select
                      className="custom-input rounded-lg"
                      value={value}
                      onChange={onChange}
                      error={errors.CompanyID?.message}
                    >
                      {companies.map((company) => (
                        <option key={company.CompanyID} value={company.CompanyID}>
                          {company.Name}
                        </option>
                      ))}
                    </select>
                  )}
                />
              </div>
            </div>
            <div className="flex justify-between mb-2">
              <div>
                <p>Driver Code</p>
              </div>
              <div className="flex justify-between w-35">
                <Controller
                  control={control}
                  name="DriverCode"
                  rules={{ required: "Driver Code is required" }}
                  render={({ field: { value, onChange } }) => (
                    <TextInput
                      value={value}
                      onChange={onChange}
                      className="custom-input rounded-lg w-100"
                      error={errors.DriverCode?.message}
                    />
                  )}
                />
              </div>
            </div>
            <div className="flex justify-between mb-2">
              <div>
                <p>First Name</p>
              </div>
              <div className="flex justify-between w-35">
                <Controller
                  control={control}
                  name="FirstName"
                  rules={{ required: "First Name is required" }}
                  render={({ field: { value, onChange } }) => (
                    <TextInput
                      value={value}
                      onChange={onChange}
                      className="custom-input rounded-lg w-100"
                      error={errors.FirstName?.message}
                    />
                  )}
                />
              </div>
            </div>
            <div className="flex justify-between mb-2">
              <div>
                <p>Last Name</p>
              </div>
              <div className="flex justify-between w-35">
                <Controller
                  control={control}
                  name="LastName"
                  rules={{ required: "Last Name is required" }}
                  render={({ field: { value, onChange } }) => (
                    <TextInput
                      value={value}
                      onChange={onChange}
                      className="custom-input rounded-lg w-100"
                      error={errors.LastName?.message}
                    />
                  )}
                />
              </div>
            </div>
            <div className="flex justify-between mb-2">
              <div>
                <p>Email</p>
              </div>
              <div className="flex justify-between w-35">
                <Controller
                  control={control}
                  name="Email"
                  rules={{ 
                    required: "Email is required",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Invalid email address"
                    }
                  }}
                  render={({ field: { value, onChange } }) => (
                    <TextInput
                      type="email"
                      value={value}
                      onChange={onChange}
                      className="custom-input rounded-lg w-100"
                      error={errors.Email?.message}
                    />
                  )}
                />
              </div>
            </div>
            <div className="flex justify-between mb-2">
              <div>
                <p>Active</p>
              </div>
              <div className="flex justify-between w-35">
                <Controller
                  control={control}
                  name="Active"
                  render={({ field: { value, onChange } }) => (
                    <input
                      type="checkbox"
                      checked={!!value}
                      onChange={(e) => onChange(e.target.checked)}
                    />
                  )}
                />
              </div>
            </div>
            <div className="flex gap-4 mt-4 save-button">
              <button
                disabled={false}
                onClick={handleSubmit(OnCreate)}
                className="justify-between p-2 pr-20 pl-20 rounded-lg mr-1 flex items-center custom-blue-button"
              >
                <span>{driver ? "Update" : "Create"}</span>
              </button>
              <div>
                <div
                  onClick={OnCancel}
                  className="justify-between pl-20 pr-20 p-2 rounded-lg flex items-center custom-white-button"
                >
                  <span>Cancel</span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div>
            <div className="flex ">
              <h5 className="mb-0">My Drivers</h5>
              <button
                className="justify-between rounded-lg ml-1 flex items-center custom-blue-button border-none"
                onClick={onAddNew}
              >
                <span>Add New</span>
              </button>
            </div>
            <SearchBar setSearch={setSearch} OnSearch={OnSearch} />
            <table className="table-auto w-full mt-4 border border-gray-200">
              <thead>
                <tr className="bg-gray-100 cursor-pointer">
                  <th className="px-4 py-2 border">ID</th>
                  <th className="px-4 py-2 border">Company Name</th>
                  <th className="px-4 py-2 border">Driver Code</th>
                  <th className="px-4 py-2 border">First Name</th>
                  <th className="px-4 py-2 border">Last Name</th>
                  <th className="px-4 py-2 border">Email</th>
                  <th className="px-4 py-2 border">Active</th>
                  <th className="px-4 py-2 border">Edit</th>
                </tr>
              </thead>
              <tbody>
                {driversData &&
                  driversData.map((type) => (
                    <tr key={type.DriverID} className="hover:bg-gray-50">
                      <td className="px-4 py-2 border">{type.DriverID}</td>
                      <td className="px-4 py-2 border">
                        {companies.find((c) => c.CompanyID === type.CompanyID)
                          ?.Name || ""}
                      </td>
                      <td className="px-4 py-2 border">{type.DriverCode}</td>
                      <td className="px-4 py-2 border">{type.FirstName}</td>
                      <td className="px-4 py-2 border">{type.LastName}</td>
                      <td className="px-4 py-2 border">{type.Email}</td>
                      <td className="px-4 py-2 border">
                        {type.Active ? "Yes" : "No"}
                      </td>
                      <td className="px-4 py-2 border cursor-point">
                        <FaEdit
                          onClick={() => {
                            setDriver(type);
                            setIsEdit(true);
                          }}
                        />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
            {driversData.length === 0 && (
              <div className="empty-state">
                No drivers assigned to you yet. Click "Add New" to add a driver.
              </div>
            )}
            <div className="pagination-container">
              <div className="pagination-content">
                <div>
                  <p className="pagination-text">
                    Showing <span>{totalCount > 0 ? (page - 1) * PAGE_SIZE + 1 : 0}</span> to{" "}
                    <span>{Math.min(totalCount, page * PAGE_SIZE)}</span> of{" "}
                    <span>{totalCount}</span> results
                  </p>
                </div>
                <div>
                  <nav className="pagination-nav">
                    <button
                      onClick={onPrevPage}
                      disabled={page === 1}
                      className={`pagination-item ${
                        page === 1 ? "disabled" : ""
                      }`}
                    >
                      <FaAngleLeft className="pagination-icon" />
                    </button>
                    <button
                      onClick={onNextPage}
                      disabled={page * PAGE_SIZE >= totalCount}
                      className={`pagination-item ${
                        page * PAGE_SIZE >= totalCount ? "disabled" : ""
                      }`}
                    >
                      <FaAngleRight className="pagination-icon" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManagerDrivers;

const SearchBar = ({ setSearch, OnSearch }) => {
  // Restore search values from sessionStorage
  const savedSearch = sessionStorage.getItem("managerDriversSearch");
  let initialValues = {
    type: "FirstName",
    text: "",
  };
  
  if (savedSearch) {
    try {
      const parsed = JSON.parse(savedSearch);
      if (parsed && parsed.type && parsed.text) {
        initialValues = parsed;
      }
    } catch {
      // Ignore parse errors
    }
  }
  
  const {
    control: searchControl,
    handleSubmit: handleSearchSubmit,
    reset: searchReset,
  } = useForm({
    defaultValues: initialValues,
  });

  return (
    <div className="flex mt-4 items-center">
      <div>
        <p className="mb-0">Look For:</p>
      </div>
      <div className="flex justify-between w-10 ml-1">
        <Controller
          control={searchControl}
          name="type"
          render={({ field: { value, onChange } }) => (
            <select
              className="custom-input rounded-lg cursor-pointer"
              value={value}
              onChange={onChange}
            >
              <option value={"FirstName"}>First Name</option>
              <option value={"LastName"}>Last Name</option>
              <option value={"Email"}>Email</option>
              <option value={"DriverCode"}>Driver Code</option>
            </select>
          )}
        />
      </div>
      <div className="ml-1">
        <Controller
          control={searchControl}
          name="text"
          rules={{ required: "Required" }}
          render={({ field: { value, onChange } }) => (
            <input
              type="text"
              value={value}
              onChange={onChange}
              className="custom-input rounded-lg w-100 "
            />
          )}
        />
      </div>
      <div className="ml-1 flex">
        <div>
          <button
            onClick={handleSearchSubmit(OnSearch)}
            className="justify-between rounded-lg flex items-center w-30 custom-search-button border-none"
          >
            <span>Search</span>
          </button>
        </div>
        <div>
          <button
            onClick={() => {
              searchReset({ type: "FirstName", text: "" });
              setSearch(null);
              // Clear saved search from sessionStorage
              sessionStorage.removeItem("managerDriversSearch");
            }}
            className="justify-between rounded-lg ml-1 flex items-center custom-reset-button"
          >
            <span>Reset</span>
          </button>
        </div>
      </div>
    </div>
  );
};